import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import Grid from '@material-ui/core/Grid'
import Chip from '@material-ui/core/Chip'
import MUISelect from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import Typography from '@material-ui/core/Typography'
import { withStyles } from '@material-ui/core/styles'


const styles = theme => ({
  root: {
    width: 200,
    display: 'flex',
    cursor: 'pointer',
    alignItems: 'center',
    background: 'white',
    height: 'auto',
    minHeight: 40,
    padding: 0,
    borderWidth: 1,
    borderRadius: 4,
    borderStyle: 'solid',
    borderColor: 'whitesmoke',
    '&:hover': {
      background: 'white',
    },
  },
  chip: {
    height: 20,
    borderRadius: 0,
    color: theme.palette.common.white,
    background: theme.palette.secondary.main,
    marginRight: 10,

    '&:last-child': {
      marginRight: 0,
    },
  },
  fullWidth: {
    width: '100%',
  },
  primary: {
    height: 48,
    borderRadius: 0,
    borderColor: theme.palette.primary.main,
  },
  label: {
    color: theme.palette.common.black,
  },
  select: {
    width: '100%',
    height: '100%',
    fontWeight: 300,
    textAlign: 'left',
    '&:after, &:before': {
      display: 'none',
    },
  },
  fullHeight: {
    minHeight: '100%',
  },
  selectIcon: {
    right: 20,
  },
  selectContent: {
    paddingTop: 2,
    paddingBottom: 2,
    height: 36,
    lineHeight: '36px',
    paddingLeft: 20,
    background: 'transparent !important',
  },
})


class Select extends PureComponent {
  /* LIFECYCLE */
  state = {
    open: false,
  }


  /* METHODS */

  handleClose = (e) => {
    const { multiple } = this.props

    if (!multiple || (multiple && e.target.tagName.toLowerCase() !== 'li')) {
      this.setState({
        open: false,
      })
    }
  }

  handleOpen = () => {
    this.setState({
      open: true,
    })
  }

  handleChange = (e) => {
    let { value } = this.props
    const { multiple, onChange, maxItems } = this.props

    if (typeof e.target.value === 'string' && multiple) {
      if (value.includes(e.target.value)) {
        value = value.filter(v => v !== e.target.value)
      } else {
        value = value.concat(e.target.value)
      }

      if (maxItems > 0) {
        value = value.slice(0, maxItems)
      }
    } else {
      // eslint-disable-next-line
      value = e.target.value
    }

    return onChange({
      target: {
        value,
        name: e.target.name,
      },
    })
  }

  /**
   * Create handler on delete multiple select
   */
  createOnDeleteHandler = valueToDelete => (e) => {
    e.stopPropagation()

    const { name, value, onChange } = this.props
    const nextValue = value.filter(x => x !== valueToDelete)

    return onChange({
      target: {
        name,
        value: nextValue,
      },
    })
  }


  /* RENDER */

  /**
   * @render
   * @returns {JSX}
   */
  render() {
    const {
      label,
      multiple,
      classes,
      primary,
      children,
      className,
      fullWidth,
      errorText,
      enableDefault,
      IconComponent,
      iconClassName,
      selectClassName,
      renderMultipleLabel,
      maxItems,
      ...props
    } = this.props
    const { open } = this.state

    return (
      <div
        className={classNames(classes.root, className, {
          [classes.primary]: primary,
          [classes.fullWidth]: fullWidth,
        })}
      >
        <MUISelect
          open={open}
          displayEmpty
          placeholder={label}
          onOpen={this.handleOpen}
          onClose={this.handleClose}
          className={classNames(classes.select, selectClassName)}
          IconComponent={IconComponent}
          classes={{
            select: classes.selectContent,
            icon: classNames(classes.selectIcon, iconClassName),
          }}
          renderValue={!multiple ? undefined : ((selected) => {
            const values = selected.filter(x => x)

            return (
              <Grid container wrap="wrap" alignItems="center" className={classes.fullHeight}>
                {values.map(value => (
                  <Chip
                    key={value}
                    className={classes.chip}
                    onClick={this.createOnDeleteHandler(value)}
                    onDelete={this.createOnDeleteHandler(value)}
                    label={renderMultipleLabel ? renderMultipleLabel(value) : value}
                  />
                ))}
                {!values.length && <Typography>{label}</Typography>}
              </Grid>
            )
          })}
          {...props}
          onChange={this.handleChange}
        >
          <MenuItem value="" disabled={!enableDefault}>
            {label}
          </MenuItem>
          {children}
        </MUISelect>

        {errorText && <Typography className="mt1" color="error">{errorText}</Typography>}
      </div>
    )
  }
}


Select.defaultProps = {
  value: '',
  maxItems: -1,
  enableDefault: false,
}


Select.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  primary: PropTypes.bool,
  children: PropTypes.node,
  onChange: PropTypes.func,
  multiple: PropTypes.bool,
  classes: PropTypes.object,
  fullWidth: PropTypes.bool,
  maxItems: PropTypes.number,
  errorText: PropTypes.string,
  className: PropTypes.string,
  IconComponent: PropTypes.func,
  enableDefault: PropTypes.bool,
  iconClassName: PropTypes.string,
  selectClassName: PropTypes.string,
  renderMultipleLabel: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
}


export default withStyles(styles)(Select)
